<template>
  <span
    class="app-icon-wrapper inline-flex shrink-0 items-center justify-center select-none"
    :style="wrapperStyle"
  >
    <!-- 1. Iconify Icon (Solid or Gradient) -->
    <DMSuiteIcon
      v-if="isIconify"
      :icon="iconifyIdentifier"
      :size="computedSize"
      :style-type="resolvedStyleType"
      :color="resolvedColor"
      :gradient="resolvedGradient"
      :custom-class="customClass"
    />

    <!-- 2. Image / SVG file -->
    <img
      v-else-if="isImage"
      :src="imageUrl"
      :alt="computedLabel"
      :style="imageStyle"
      :class="['app-icon-image object-contain rounded', customClass]"
      loading="lazy"
    >

    <!-- 3. FontAwesome / CSS class fallback -->
    <i
      v-else
      :class="[effectiveIconString || 'fas fa-circle', customClass]"
      :style="legacyFontStyle"
      class="app-icon-font"
    />
  </span>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import DMSuiteIcon from '@/components/iconify/DMSuiteIcon.vue';
import {
  isSidenavImageIcon,
  resolveSidenavIconUrl,
} from '@/utils/sidenavIcons';
import {
  DEFAULT_ICON_COLOR,
  DEFAULT_ICON_SIZE,
  FALLBACK_ICON_NAME,
  type IconGradientConfig,
} from '@/config/dmSuiteIconPalette';

export interface AppIconItemProps {
  icon?: string | null;
  icon_type?: string | null;
  icon_name?: string | null;
  icon_color?: string | null;
  icon_size?: number | null;
  icon_style?: 'solid' | 'gradient' | string | null;
  icon_gradient?: IconGradientConfig | string | null;
  label?: string;
}

interface Props {
  app?: AppIconItemProps | null;
  icon?: string | null;
  iconType?: string | null;
  iconName?: string | null;
  iconColor?: string | null;
  iconSize?: number | null;
  iconStyle?: 'solid' | 'gradient' | string | null;
  iconGradient?: IconGradientConfig | string | null;
  styleType?: 'solid' | 'gradient' | string | null;
  gradient?: IconGradientConfig | string | null;
  size?: number | string | null;
  color?: string | null;
  label?: string;
  customClass?: string;
}

const props = withDefaults(defineProps<Props>(), {
  app: null,
  icon: null,
  iconType: null,
  iconName: null,
  iconColor: null,
  iconSize: null,
  iconStyle: null,
  iconGradient: null,
  styleType: null,
  gradient: null,
  size: null,
  color: null,
  label: '',
  customClass: '',
});

const resolvedType = computed(() => {
  return props.iconType || props.app?.icon_type || null;
});

const resolvedName = computed(() => {
  return props.iconName || props.app?.icon_name || null;
});

const resolvedColor = computed(() => {
  return props.color || props.iconColor || props.app?.icon_color || DEFAULT_ICON_COLOR;
});

const resolvedStyleType = computed(() => {
  return (
    props.styleType ||
    props.iconStyle ||
    props.app?.icon_style ||
    (resolvedGradient.value ? 'gradient' : 'solid')
  );
});

const resolvedGradient = computed(() => {
  return props.gradient || props.iconGradient || props.app?.icon_gradient || null;
});

const resolvedSize = computed(() => {
  if (props.size !== null && props.size !== undefined) {
    return Number(props.size);
  }
  if (props.iconSize !== null && props.iconSize !== undefined) {
    return Number(props.iconSize);
  }
  if (props.app?.icon_size !== null && props.app?.icon_size !== undefined) {
    return Number(props.app.icon_size);
  }
  return DEFAULT_ICON_SIZE;
});

const computedSize = computed(() => resolvedSize.value);

const rawIcon = computed(() => {
  return props.icon || props.app?.icon || '';
});

const isIconify = computed(() => {
  if (resolvedType.value === 'iconify') return true;
  if (resolvedName.value && resolvedName.value.includes(':')) return true;
  const str = String(rawIcon.value || '').trim();
  if (!str) return false;
  if (isSidenavImageIcon(str)) return false;
  if (str.startsWith('fa') || str.includes(' fa-')) return false;
  return str.includes(':') && !str.includes('/') && !str.includes('.');
});

const iconifyIdentifier = computed(() => {
  if (resolvedName.value) {
    return resolvedName.value;
  }
  const str = String(rawIcon.value || '').trim();
  if (str && str.includes(':') && !str.includes('/')) {
    return str;
  }
  return FALLBACK_ICON_NAME;
});

const isImage = computed(() => {
  if (isIconify.value) return false;
  return isSidenavImageIcon(rawIcon.value);
});

const imageUrl = computed(() => {
  return resolveSidenavIconUrl(rawIcon.value);
});

const effectiveIconString = computed(() => {
  return rawIcon.value;
});

const computedLabel = computed(() => {
  return props.label || props.app?.label || 'App Icon';
});

const wrapperStyle = computed(() => ({
  width: `${computedSize.value}px`,
  height: `${computedSize.value}px`,
}));

const legacyFontStyle = computed(() => {
  const styles: Record<string, string> = {};
  if (resolvedColor.value) {
    styles.color = resolvedColor.value;
  }
  return styles;
});

const imageStyle = computed(() => ({
  width: `${computedSize.value}px`,
  height: `${computedSize.value}px`,
}));
</script>

<style scoped>
.app-icon-wrapper {
  vertical-align: middle;
}
</style>
